import { ESCALATION_STATES, targetShape } from "./escalationClient.js";

const MINUTE = 60;
const HOUR = 3600;
const DAY = 86400;

export function formatWindow(seconds) {
    const total = Math.max(0, Math.round(Number(seconds) || 0));
    if (total < MINUTE) return `${total} second${total === 1 ? "" : "s"}`;
    const days = Math.floor(total / DAY);
    const hours = Math.floor((total % DAY) / HOUR);
    const minutes = Math.floor((total % HOUR) / MINUTE);
    const parts = [];
    if (days) parts.push(`${days} day${days === 1 ? "" : "s"}`);
    if (hours) parts.push(`${hours} hour${hours === 1 ? "" : "s"}`);
    if (minutes && !days) parts.push(`${minutes} minute${minutes === 1 ? "" : "s"}`);
    return parts.join(" ") || "0 minutes";
}

export function formatCountdown(msRemaining) {
    const seconds = Math.max(0, Math.floor((Number(msRemaining) || 0) / 1000));
    if (seconds <= 0) return "ended";
    if (seconds < MINUTE) return `${seconds}s left`;
    if (seconds < HOUR) return `${Math.floor(seconds / MINUTE)}m left`;
    if (seconds < DAY) {
        const hours = Math.floor(seconds / HOUR);
        const minutes = Math.floor((seconds % HOUR) / MINUTE);
        return minutes ? `${hours}h ${minutes}m left` : `${hours}h left`;
    }
    const days = Math.floor(seconds / DAY);
    const hours = Math.floor((seconds % DAY) / HOUR);
    return hours ? `${days}d ${hours}h left` : `${days}d left`;
}

export function countdownTone(msRemaining) {
    const minutes = (Number(msRemaining) || 0) / 60000;
    if (minutes <= 0) return "ended";
    if (minutes <= 15) return "ending";
    return "live";
}

export function displayState(request, now = Date.now()) {
    if (request?.state !== ESCALATION_STATES.APPROVED) return request?.state ?? "pending";
    if (request.revokedAt) return ESCALATION_STATES.REVOKED;
    if (request.expiresAt && request.expiresAt.getTime() <= now) return "expired";
    return ESCALATION_STATES.APPROVED;
}

export const STATE_COPY = Object.freeze({
    pending: { label: "Awaiting decision", dot: "bg-[color:var(--amber,#c9a227)]" },
    approved: { label: "Active", dot: "bg-[color:var(--green)]" },
    expired: { label: "Expired", dot: "bg-[color:var(--muted-soft)]" },
    denied: { label: "Denied", dot: "bg-red-400" },
    cancelled: { label: "Withdrawn", dot: "bg-[color:var(--muted-soft)]" },
    revoked: { label: "Revoked", dot: "bg-red-400" },
});

export function stateCopy(state) {
    return STATE_COPY[state] ?? STATE_COPY.pending;
}

const SHAPE_COPY = Object.freeze({
    person: {
        kind: "One person",
        sentence: "Scope: This person's activity across their machines",
    },
    person_on_machine: {
        kind: "One person, one machine",
        sentence: "Scope: All users on this machine while owned by this person",
    },
    machine: {
        kind: "One machine",
        sentence: "Scope: All users on this machine",
    },
    none: { kind: "No target", sentence: "Choose a person or a machine to continue" },
});

export function describeTargetShape(target) {
    return SHAPE_COPY[targetShape(target)] ?? SHAPE_COPY.none;
}

export function formatAgo(date, now = Date.now()) {
    if (!(date instanceof Date)) return "";
    const seconds = Math.round((now - date.getTime()) / 1000);
    if (seconds < 45) return "just now";
    if (seconds < HOUR) {
        const minutes = Math.round(seconds / MINUTE);
        return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    }
    if (seconds < DAY) {
        const hours = Math.round(seconds / HOUR);
        return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    }
    const days = Math.round(seconds / DAY);
    return `${days} day${days === 1 ? "" : "s"} ago`;
}

export function shortId(id) {
    const value = String(id ?? "");
    return value.length > 10 ? `${value.slice(0, 10)}…` : value;
}
